import {
  ChangeDetectionStrategy,
  Component,
  Input,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgCommonModule } from '@principle-theorem/ng-shared';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ConfirmDialogComponent,
  type IConfirmationDialogInput,
  confirmationDialogData,
} from '@principle-theorem/ng-shared';

import {
  ContentContainerComponent,
  OrganisationService,
} from '@principle-theorem/ng-principle-shared';
import { createInsertPatientDocumentMenuButton } from '@principle-theorem/ng-patient';
import { buildReferralTemplatesWithContext } from '../../helpers/build-referral-templates-with-context';
import {
  BreadcrumbsComponent,
  DialogPresets,
  IBreadcrumb,
  NG_SHARED_CONFIG,
  NgFireMediaUploader,
} from '@principle-theorem/ng-shared';

import {
  IBrand,
  IReferral,
  IReferralClinicalNotesChanged,
  MentionResourceType,
  ReferralActor,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  WithRef,
  filterUndefined,
  getError,
  snapshot,
  snapshotDefined,
  toNamedTypeDocument,
} from '@principle-theorem/shared';
import { MixedSchema } from '@principle-theorem/editor';
import { resolveReferralEmailTemplateDefaults } from '../../helpers/resolve-referral-email-template-defaults';
import { Observable, ReplaySubject, Subject, combineLatest } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { ReferralAttachmentMenuComponent } from '../../components/referral-attachment-menu/referral-attachment-menu.component';
import { ReferralCommunicationComponent } from '../../components/referral-communication/referral-communication.component';
import { ReferralImageUploadComponent } from '../../components/referral-image-upload/referral-image-upload.component';
import { ReferralMediaComponent } from '../../components/referral-media/referral-media.component';
import { ReferralMenuButtonComponent } from '../../components/referral-menu-button/referral-menu-button.component';
import { ReferralParticipantDetailsComponent } from '../../components/referral-participant-details/referral-participant-details.component';
import { ReferralPatientDetailsComponent } from '../../components/referral-patient-details/referral-patient-details.component';
import { ReferralPreviewDialogComponent } from '../../components/referral-preview-dialog/referral-preview-dialog.component';
import { ReferralStatusComponent } from '../../components/referral-status/referral-status.component';
import { ReferralFollowersComponent } from '../../components/referral-followers/referral-followers.component';
import { ReferralToolbarComponent } from '../../components/referral-toolbar/referral-toolbar.component';
import { ReferralPatientLetterComponent } from '../../components/referral-patient-letter/referral-patient-letter.component';
import {
  IReferralComposeEmailDialogData,
  IReferralComposeEmailDialogResult,
  ReferralComposeEmailDialogComponent,
} from '../../components/referral-compose-email-dialog/referral-compose-email-dialog.component';
import { ReferralClinicalNotesService } from '../../services/referral-clinical-notes.service';
import { ReferralEmailService } from '../../services/referral-email.service';
import { ReferralMediaService } from '../../services/referral-media.service';
import { ReferralService } from '../../services/referral.service';
import { ReferralListStore } from '../../stores/referral-list.store';
import { ReferralStore } from '../../stores/referral.store';
import {
  createInsertTemplateMenuButton,
  EditorPresetsService,
} from '@principle-theorem/ng-interactions';
import { Storage } from '@angular/fire/storage';
import { MenuButtonLoaderFn } from '@principle-theorem/ng-prosemirror';

@Component({
  selector: 'pr-referral',
  imports: [
    NgCommonModule,
    BreadcrumbsComponent,
    ContentContainerComponent,
    MatProgressBarModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    ReferralToolbarComponent,
    ReferralPatientDetailsComponent,
    ReferralParticipantDetailsComponent,
    ReferralCommunicationComponent,
    ReferralMediaComponent,
    ReferralStatusComponent,
    ReferralMenuButtonComponent,
    MatTabsModule,
    ReferralAttachmentMenuComponent,
    ReferralImageUploadComponent,
    ReferralPatientLetterComponent,
    ReferralFollowersComponent,
  ],
  templateUrl: './referral.component.html',
  styleUrl: './referral.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    ReferralStore,
    ReferralEmailService,
    ReferralClinicalNotesService,
  ],
})
export class ReferralComponent {
  private _dialog = inject(MatDialog);
  private _referralUid$ = new ReplaySubject<string>(1);
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);
  private _editorPresets = inject(EditorPresetsService);
  private _storage = inject(Storage);
  private _sharedConfig = inject(NG_SHARED_CONFIG);
  public referralService = inject(ReferralService);
  public referralClinicalNotesService = inject(ReferralClinicalNotesService);
  public referralEmailService = inject(ReferralEmailService);
  public referralMediaService = inject(ReferralMediaService);
  public organisation = inject(OrganisationService);
  public store = inject(ReferralStore);
  public referralListStore = inject(ReferralListStore);

  breadcrumbs$: Observable<IBreadcrumb[]>;
  menuItems = signal<MenuButtonLoaderFn[]>([]);

  @Input({ required: true })
  set referralUid(referralUid: string) {
    if (referralUid) {
      this._referralUid$.next(referralUid);
    }
  }

  constructor() {
    this.breadcrumbs$ = this.store.referral$.pipe(
      filterUndefined(),
      take(1),
      map((referral) => [
        { label: 'Referrals', path: '../' },
        { label: referral.uid },
      ])
    );

    this._loadReferral(
      this._referralUid$,
      this.organisation.brand$.pipe(filterUndefined())
    );

    combineLatest([
      this.store.referral$.pipe(filterUndefined()),
      this.organisation.staffer$.pipe(filterUndefined()),
    ])
      .pipe(takeUntilDestroyed(), take(1))
      .subscribe(
        ([referral, staffer]) =>
          void this.referralService.markAsRead(
            referral,
            toNamedTypeDocument(staffer, MentionResourceType.Staffer)
          )
      );

    this.store.referral$
      .pipe(filterUndefined(), take(1), takeUntilDestroyed())
      .subscribe((referral) => {
        this.store.setLoading(true);
        this.store.loadPatient(referral);
        this.store.loadReferrer(referral);
        this.store.loadRecipient(referral);
        this.store.loadPractice(referral);
        this.store.loadMessages(referral);
        this.store.loadInteractions(referral);
        this.store.loadMedia(referral);
        this.store.loadClinicalNotes(referral);
        this.store.loadNotes(referral);
        this.store.setLoading(false);
      });

    this.store.patient$
      .pipe(filterUndefined(), take(1), takeUntilDestroyed())
      .subscribe((patient) => {
        this.store.setLoading(true);
        this.store.loadPrimaryContact(patient);
        this.store.loadPatientContactNumbers(patient);
        this.store.setLoading(false);
      });

    this.organisation.staffer$
      .pipe(filterUndefined(), takeUntilDestroyed())
      .subscribe((staffer) =>
        this.store.setCurrentUser(
          toNamedTypeDocument(staffer, MentionResourceType.Staffer)
        )
      );

    this.store.setupAutoSave();

    const uploader = new NgFireMediaUploader(
      this._storage,
      this.organisation.storagePath$.pipe(filterUndefined())
    );

    combineLatest([
      this.store.patient$.pipe(filterUndefined()),
      this.store.practice$.pipe(filterUndefined()),
      this.store.referral$.pipe(filterUndefined()),
    ])
      .pipe(take(1), takeUntilDestroyed())
      .subscribe(([patient, practice, referral]) => {
        const menuCoordinator$ = new Subject<string>();
        this.menuItems.set(
          this._editorPresets.defaultMenuItems(uploader, [
            createInsertTemplateMenuButton({
              templates$: buildReferralTemplatesWithContext(
                referral,
                practice,
                this._sharedConfig.appUrl,
                this.organisation.brand$.pipe(filterUndefined())
              ),
              menuCoordinator$,
            }),
            createInsertPatientDocumentMenuButton({
              patient,
              menuCoordinator$,
            }),
          ])
        );
      });
  }

  async preview(): Promise<void> {
    const referral = await snapshot(this.store.referral$);
    if (!referral) {
      return;
    }

    this._dialog.open(
      ReferralPreviewDialogComponent,
      DialogPresets.fullscreen({ data: { referral } })
    );
  }

  async handleClinicalNotesChanged(
    clinicalNotes: IReferralClinicalNotesChanged
  ): Promise<void> {
    const referral = await snapshot(this.store.referral$);
    const currentUser = await snapshot(this.store.currentUser$);
    if (!referral || !currentUser) {
      return;
    }

    await this.referralClinicalNotesService.updateClinicalNotes(
      referral,
      currentUser,
      clinicalNotes
    );
  }

  async deleteReferral(referral: WithRef<IReferral>): Promise<void> {
    try {
      await this.referralService.delete(referral);
      this.store.reset();
      this.referralListStore.removeReferral(referral);
      await this._router.navigate(['../'], { relativeTo: this._route });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(getError(error));
    }
  }

  async sendReferral(): Promise<void> {
    const referral = await snapshot(this.store.referral$);
    const currentUser = await snapshot(this.store.currentUser$);
    const patient = await snapshot(this.store.patient$);
    const practice = await snapshot(this.store.practice$);
    const messages = await snapshot(this.store.messages$);
    const media = await snapshot(this.store.media$);
    const clinicalNotes = await snapshot(this.store.clinicalNotes$);

    if (!referral || !currentUser || !patient || !practice) {
      return;
    }

    if (!messages?.length && !media?.length && !clinicalNotes?.length) {
      const confirmationData = confirmationDialogData({
        title: 'Send Empty Referral?',
        prompt:
          'This referral has no messages, attachments, or clinical notes. Are you sure you want to send it?',
        submitLabel: 'Send Anyway',
        submitColor: 'warn',
      });

      const confirmed = await this._dialog
        .open<ConfirmDialogComponent, IConfirmationDialogInput, boolean>(
          ConfirmDialogComponent,
          DialogPresets.small({ data: confirmationData })
        )
        .afterClosed()
        .toPromise();

      if (!confirmed) {
        return;
      }
    }

    let defaultContent: MixedSchema | undefined;
    let defaultSubject: string | undefined;

    try {
      ({ defaultContent, defaultSubject } =
        await resolveReferralEmailTemplateDefaults(
          referral,
          practice,
          this._sharedConfig.appUrl
        ));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(getError(error));
    }

    const dialogData: IReferralComposeEmailDialogData = {
      referral,
      patient,
      practice,
      currentUser,
      recipientEmail: referral.externalUserEmail,
      recipientName: referral.recipient?.name,
      defaultContent,
      defaultSubject,
    };

    const result = await this._dialog
      .open<
        ReferralComposeEmailDialogComponent,
        IReferralComposeEmailDialogData,
        IReferralComposeEmailDialogResult
      >(
        ReferralComposeEmailDialogComponent,
        DialogPresets.medium({ data: dialogData })
      )
      .afterClosed()
      .toPromise();

    if (!result) {
      return;
    }

    await this._sendReferralEmail(referral, currentUser, result);
  }

  private async _sendReferralEmail(
    referral: WithRef<IReferral>,
    currentUser: ReferralActor,
    emailData: IReferralComposeEmailDialogResult
  ): Promise<void> {
    const brand = await snapshotDefined(this.organisation.brand$);

    try {
      await this.referralEmailService.sendReferralEmail(
        brand.ref,
        referral,
        currentUser,
        emailData.subject,
        emailData.content
      );

      const fresh = await Firestore.getDoc(referral.ref);
      this.store.setReferral(fresh);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(getError(error));
    }
  }

  private _loadReferral(
    id$: Observable<string>,
    brand$: Observable<WithRef<IBrand>>
  ): void {
    combineLatest([
      id$,
      brand$,
      id$.pipe(switchMap((id) => this.referralListStore.getReferral$(id))),
    ])
      .pipe(take(1), takeUntilDestroyed())
      .subscribe(([id, brand, referral]) =>
        this.store.loadReferral({ id, brand, referral })
      );
  }
}
